package com.bitterinfant.webui

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * A small "i" info button that reveals [text] in a floating bubble. Unlike the native `title`
 * attribute, this works on touch devices too: mouse users get it on hover, touch users get it
 * on tap (tapping again, or tapping elsewhere via [onMouseLeave]-less devices, tap the icon
 * again to dismiss).
 *
 * [alignEnd] anchors the bubble's right edge (instead of its left edge) to the icon — use this
 * when the icon sits near the right edge of its container (e.g. overlaid on a video corner) so
 * the bubble opens inward instead of running off-screen.
 */
@Composable
fun TooltipIcon(text: String, alignEnd: Boolean = false) {
    var show by remember { mutableStateOf(false) }
    Span(attrs = {
        style {
            position(Position.Relative)
            // Flex (not inline-block) so the button isn't baseline-aligned inside a line box
            // taller than itself — that left it riding a few px below [CopyLinkButton]'s icon.
            property("display", "inline-flex")
            alignItems(AlignItems.Center)
            marginLeft(0.4.cssRem)
            property("flex-shrink", "0")
        }
    }) {
        Button(attrs = {
            attr("type", "button")
            attr("aria-label", "More info: $text")
            onClick { show = !show }
            onMouseEnter { show = true }
            onMouseLeave { show = false }
            style { roundIconButton() }
        }) { Text("i") }
        if (show) {
            Div(attrs = {
                style {
                    position(Position.Absolute)
                    top(1.6.cssRem)
                    if (alignEnd) right(0.px) else left(0.px)
                    property("z-index", "10")
                    minWidth(160.px)
                    maxWidth(260.px)
                    padding(0.6.cssRem, 0.8.cssRem)
                    property("background", Theme.bgElevated)
                    property("border", "1px solid ${Theme.border}")
                    property("border-radius", "6px")
                    property("box-shadow", "0 4px 12px rgba(0,0,0,0.4)")
                    fontSize(0.8.cssRem)
                    property("line-height", "1.4")
                    color(Color(Theme.text))
                }
            }) { Text(text) }
        }
    }
}

/** The small outlined circle shared by [TooltipIcon]'s "i" and [CopyLinkButton]'s link icon. */
internal fun StyleScope.roundIconButton() {
    property("display", "inline-flex")
    alignItems(AlignItems.Center)
    justifyContent(JustifyContent.Center)
    width(1.2.cssRem)
    height(1.2.cssRem)
    property("border-radius", "50%")
    property("border", "1px solid ${Theme.textMuted}")
    // Semi-opaque (not fully transparent) so the icon stays legible when overlaid
    // on a video thumbnail, not just the page's own dark background.
    property("background", "rgba(11, 11, 13, 0.55)")
    property("cursor", "pointer")
    padding(0.px)
    fontSize(0.7.cssRem)
    property("line-height", "1")
    color(Color(Theme.textMuted))
    property("font-family", Theme.fontStack)
}
